import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VisibilityMatrix = ({ currentPlan, onSelectPlan, onUpgrade }) => {
  const [selectedPlan, setSelectedPlan] = useState(currentPlan);

  const visibilityPlans = [
    {
      id: 'department',
      name: 'Department',
      scope: 'Department Level',
      price: 2000,
      reach: '5,000-15,000',
      coverage: 'Your department only',
      features: ['Local visibility', 'Department search results', 'Basic analytics'],
      icon: 'MapPin',
      popular: false,
      description: 'Perfect for local sellers targeting nearby customers'
    },
    {
      id: 'regional',
      name: 'Regional',
      scope: 'Regional Level',
      price: 8000,
      reach: '50,000-150,000',
      coverage: 'Your region + neighboring departments',
      features: ['Regional visibility', 'Cross-department reach', 'Enhanced analytics', 'Priority support'],
      icon: 'Map',
      popular: true,
      description: 'Most popular choice for growing businesses'
    },
    {
      id: 'national',
      name: 'National',
      scope: 'National Level',
      price: 25000,
      reach: '500,000-1,500,000',
      coverage: 'All of Cameroon',
      features: ['National visibility', 'Country-wide reach', 'Advanced analytics', 'Priority support', 'Featured listings'],
      icon: 'Globe2',
      popular: false,
      description: 'Ideal for established dealers and large inventories'
    },
    {
      id: 'worldwide',
      name: 'Worldwide',
      scope: 'International Level',
      price: 100000,
      reach: '2,000,000+',
      coverage: 'Global visibility including neighboring countries',
      features: ['International visibility', 'Export opportunities', 'Premium analytics', 'Dedicated support', 'Featured listings', 'Professional badge'],
      icon: 'Globe',
      popular: false,
      description: 'For exporters and international dealers'
    }
  ];

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-CM', {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
    onSelectPlan(planId);
  };

  const isCurrentPlan = (planId) => planId === currentPlan;
  const isUpgrade = (planId) => {
    const currentIndex = visibilityPlans?.findIndex(p => p?.id === currentPlan);
    const selectedIndex = visibilityPlans?.findIndex(p => p?.id === planId);
    return selectedIndex > currentIndex;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-foreground mb-2">Geographic Visibility Plans</h2>
        <p className="text-muted-foreground">Choose your visibility scope to reach the right customers</p>
      </div>
      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {visibilityPlans?.map((plan) => (
          <div
            key={plan?.id}
            className={`relative border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
              selectedPlan === plan?.id
                ? 'border-primary bg-primary/5 shadow-floating'
                : isCurrentPlan(plan?.id)
                ? 'border-success bg-success/5' :'border-border hover:border-primary/50 hover:shadow-soft'
            }`}
            onClick={() => handlePlanSelect(plan?.id)}
          >
            {/* Popular Badge */}
            {plan?.popular && (
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                <span className="bg-accent text-accent-foreground text-xs font-medium px-2 py-1 rounded-full">
                  Most Popular
                </span>
              </div>
            )}

            {/* Current Plan Badge */}
            {isCurrentPlan(plan?.id) && (
              <div className="absolute -top-2 right-2">
                <div className="bg-success text-success-foreground text-xs font-medium px-2 py-1 rounded-full flex items-center space-x-1">
                  <Icon name="Check" size={12} />
                  <span>Current</span>
                </div>
              </div>
            )}

            <div className="text-center">
              {/* Icon */}
              <div className={`w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center ${
                selectedPlan === plan?.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
              }`}>
                <Icon name={plan?.icon} size={24} />
              </div>

              {/* Plan Name */}
              <h3 className="font-semibold text-foreground mb-1">{plan?.name}</h3>
              <p className="text-sm text-muted-foreground mb-3">{plan?.scope}</p>

              {/* Price */}
              <div className="mb-4">
                <p className="text-2xl font-bold text-foreground">{formatAmount(plan?.price)}</p>
                <p className="text-xs text-muted-foreground">per month</p>
              </div>

              {/* Reach */}
              <div className="mb-4 p-2 bg-muted/50 rounded">
                <p className="text-sm font-medium text-foreground">Estimated Reach</p>
                <p className="text-sm text-muted-foreground">{plan?.reach} users</p>
              </div>

              {/* Coverage */}
              <p className="text-xs text-muted-foreground mb-4">{plan?.coverage}</p>

              {/* Features */}
              <div className="space-y-2 mb-4">
                {plan?.features?.slice(0, 3)?.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2 text-xs">
                    <Icon name="Check" size={12} className="text-success" />
                    <span className="text-muted-foreground">{feature}</span>
                  </div>
                ))}
                {plan?.features?.length > 3 && (
                  <p className="text-xs text-muted-foreground">+{plan?.features?.length - 3} more features</p>
                )}
              </div>

              {/* Action Button */}
              {isCurrentPlan(plan?.id) ? (
                <Button variant="outline" size="sm" disabled fullWidth>
                  Current Plan
                </Button>
              ) : (
                <Button
                  variant={selectedPlan === plan?.id ? "default" : "outline"}
                  size="sm"
                  fullWidth
                  onClick={(e) => {
                    e?.stopPropagation();
                    handlePlanSelect(plan?.id);
                  }}
                >
                  {isUpgrade(plan?.id) ? 'Upgrade' : 'Downgrade'}
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
      {/* Plan Comparison Table - Desktop */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full border border-border rounded-lg">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-4 font-medium text-foreground">Feature</th>
              {visibilityPlans?.map((plan) => (
                <th key={plan?.id} className="text-center p-4 font-medium text-foreground">
                  {plan?.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-border">
              <td className="p-4 font-medium text-foreground">Monthly Price</td>
              {visibilityPlans?.map((plan) => (
                <td key={plan?.id} className="text-center p-4 text-foreground">
                  {formatAmount(plan?.price)}
                </td>
              ))}
            </tr>
            <tr className="border-t border-border bg-muted/25">
              <td className="p-4 font-medium text-foreground">Estimated Reach</td>
              {visibilityPlans?.map((plan) => (
                <td key={plan?.id} className="text-center p-4 text-foreground">
                  {plan?.reach}
                </td>
              ))}
            </tr>
            <tr className="border-t border-border">
              <td className="p-4 font-medium text-foreground">Geographic Coverage</td>
              {visibilityPlans?.map((plan) => (
                <td key={plan?.id} className="text-center p-4 text-muted-foreground text-sm">
                  {plan?.coverage}
                </td>
              ))}
            </tr>
            <tr className="border-t border-border bg-muted/25">
              <td className="p-4 font-medium text-foreground">Analytics</td>
              {visibilityPlans?.map((plan) => (
                <td key={plan?.id} className="text-center p-4">
                  <Icon 
                    name="Check" 
                    size={16} 
                    className={plan?.id === 'department' ? 'text-muted-foreground' : 'text-success'} 
                  />
                </td>
              ))}
            </tr>
            <tr className="border-t border-border">
              <td className="p-4 font-medium text-foreground">Priority Support</td>
              {visibilityPlans?.map((plan) => (
                <td key={plan?.id} className="text-center p-4">
                  <Icon 
                    name={plan?.id === 'department' ? 'X' : 'Check'} 
                    size={16} 
                    className={plan?.id === 'department' ? 'text-muted-foreground' : 'text-success'} 
                  />
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      {/* Selected Plan Summary */}
      {selectedPlan && selectedPlan !== currentPlan && (
        <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-medium text-foreground mb-1">
                {isUpgrade(selectedPlan) ? 'Upgrade to' : 'Switch to'} {visibilityPlans?.find(p => p?.id === selectedPlan)?.name}
              </h3>
              <p className="text-sm text-muted-foreground">
                {visibilityPlans?.find(p => p?.id === selectedPlan)?.description}
              </p>
            </div>
            <Button
              variant="default"
              onClick={() => onUpgrade(selectedPlan)}
              iconName="ArrowRight"
              iconPosition="right"
              iconSize={16}
            >
              Continue
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisibilityMatrix;